/**
 * Тема интерфейса: светлая «Аврора», тёмная или «как в системе». Выбор
 * (`preference`) хранится локально; `mode` — итоговая тема, которую читают
 * компоненты. При «системной» `mode` следует за настройкой ОС и меняется
 * на лету, если пользователь переключит тему системы при открытом
 * приложении.
 *
 * Тема — это CSS-переменные на `<html>` (их читают Tailwind и стили) и
 * `data-theme` (для `dark:` и точечных правил). Mantine следует за стором
 * через `useMantineThemeSync` в корне приложения.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { themeVars, type ThemeMode } from '../theme/tokens';

export type ThemePreference = ThemeMode | 'system';

interface ThemeStore {
  readonly preference: ThemePreference;
  /** Итоговая тема: выбранная или системная. */
  readonly mode: ThemeMode;
  toggle: () => void;
  setPreference: (preference: ThemePreference) => void;
}

const darkQuery = (): MediaQueryList | null => (typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null);

function systemMode(): ThemeMode {
  return darkQuery()?.matches === true ? 'dark' : 'light';
}

function resolve(preference: ThemePreference): ThemeMode {
  return preference === 'system' ? systemMode() : preference;
}

const PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

export function applyTheme(mode: ThemeMode): void {
  const root = document.documentElement;
  for (const [name, value] of Object.entries(themeVars(mode))) root.style.setProperty(name, value);
  root.dataset.theme = mode;
  root.style.colorScheme = mode;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      preference: 'system',
      mode: systemMode(),
      // Быстрый переключатель в шапке: явная тема, противоположная текущей.
      toggle: () => {
        const next: ThemeMode = get().mode === 'dark' ? 'light' : 'dark';
        set({ preference: next, mode: next });
      },
      setPreference: (preference) => set({ preference, mode: resolve(preference) }),
    }),
    {
      name: 'aptile-theme',
      version: 1,
      // Хранится только выбор; итоговая тема пересчитывается при загрузке.
      partialize: (state) => ({ preference: state.preference }),
      // v0 хранил итоговую тему `mode` — она и становится выбором.
      migrate: (persisted) => {
        const old = (persisted ?? {}) as { readonly mode?: unknown; readonly preference?: unknown };
        const preference = PREFERENCES.find((candidate) => candidate === old.preference || candidate === old.mode) ?? 'system';
        return { preference };
      },
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as { readonly preference?: unknown };
        const preference = PREFERENCES.find((candidate) => candidate === saved.preference) ?? current.preference;
        return { ...current, preference, mode: resolve(preference) };
      },
    },
  ),
);

applyTheme(useThemeStore.getState().mode);
useThemeStore.subscribe((state, previous) => {
  if (state.mode !== previous.mode) applyTheme(state.mode);
});

// Смена темы в ОС при открытом приложении — подхватываем, если выбрана «системная».
darkQuery()?.addEventListener('change', (event) => {
  const { preference, mode } = useThemeStore.getState();
  const next: ThemeMode = event.matches ? 'dark' : 'light';
  if (preference === 'system' && mode !== next) useThemeStore.setState({ mode: next });
});
